import React from 'react';
import {
  CheckCircle,
  Copy,
  DotsThree,
  PaperPlaneTilt,
  Pencil,
  Prohibit,
  Receipt,
  Trash,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { TableCell, TableRow } from '../../components/ui/table';
import Edi277Badge from './Edi277Badge';
import { formatCurrency, formatDate, formatTime, getStatusColor } from '../../lib/utils';
import { SELECTABLE_STATUSES, inferReasonChip } from './useClaims';

const ReasonChip = ({ claim, onClick }) => {
  const chip = inferReasonChip(claim);
  if (!chip) return null;
  const toneClass = chip.tone === 'red'
    ? 'bg-red-50 text-red-700 border-red-100'
    : 'bg-amber-50 text-amber-700 border-amber-100';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mt-1 block text-[10px] font-medium border rounded px-1.5 py-0.5 ${toneClass} max-w-[200px] truncate text-left hover:underline`}
      title={`${chip.code ? `${chip.code} · ` : ''}${chip.label} (click to see details)`}
      data-testid={`reason-chip-${claim.id}`}
    >
      {chip.code && <span className="font-mono">{chip.code}</span>}
      {chip.code && ' · '}
      {chip.label}
    </button>
  );
};

const RowActionsMenu = ({
  claim, onEdit, onCopy, onSubmit, onDelete, onAdjudication, onTriage, onCorrect, onVoid, onMarkFixed,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`claim-menu-${claim.id}`}>
        <DotsThree size={18} />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={onAdjudication} data-testid={`view-adjudication-${claim.id}`}>
        <Receipt size={14} className="mr-2 text-indigo-600" />
        View adjudication
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onCopy}>
        <Copy size={14} className="mr-2" />
        Copy Claim (Recycle)
      </DropdownMenuItem>
      {claim.status === 'Draft' && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onEdit} data-testid={`edit-claim-${claim.id}`}>
            <Pencil size={14} className="mr-2 text-indigo-600" />
            Edit Draft
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onSubmit}>
            <PaperPlaneTilt size={14} className="mr-2" />
            Submit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-red-600">
            <Trash size={14} className="mr-2" />
            Delete
          </DropdownMenuItem>
        </>
      )}
      {(claim.submitted_icn || ['Submitted', 'Accepted', 'Paid', 'Rejected'].includes(claim.status)) && claim.triage_status !== 'voided' && claim.status !== 'Voided' && (
        <>
          <DropdownMenuSeparator />
          <div className="px-2 py-1 text-[10px] font-semibold text-slate-500 uppercase">ODM Adjustments</div>
          <DropdownMenuItem onClick={onCorrect} data-testid={`correct-claim-${claim.id}`}>
            <Pencil size={14} className="mr-2 text-indigo-600" />
            Correct
            <span className="ml-auto text-[10px] text-slate-400">→ Freq 7</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onVoid} className="text-red-600" data-testid={`void-claim-${claim.id}`}>
            <Prohibit size={14} className="mr-2" />
            Void
            <span className="ml-auto text-[10px] text-red-300">→ Freq 8</span>
          </DropdownMenuItem>
        </>
      )}
      {(claim.status === 'Rejected' || claim.status === 'Denied') && claim.triage_status !== 'resolved' && claim.triage_status !== 'voided' && (
        <>
          <DropdownMenuSeparator />
          <div className="px-2 py-1 text-[10px] font-semibold text-slate-500 uppercase">Resubmit / Triage</div>
          <DropdownMenuItem onClick={() => onTriage('fix')} data-testid={`triage-fix-${claim.id}`}>
            <Pencil size={14} className="mr-2 text-indigo-600" />
            Fix the claim
            <span className="ml-auto text-[10px] text-slate-400">→ Draft</span>
          </DropdownMenuItem>
          {/* P6 (May 2026 Rhinobill parity): Mark as Fixed — keeps the
              claim in flight for tracking without resetting to Draft.
              On next 277CA accept, status elevates to "Fix Successful". */}
          <DropdownMenuItem onClick={onMarkFixed} data-testid={`mark-fixed-${claim.id}`}>
            <CheckCircle size={14} className="mr-2 text-blue-600" />
            Mark as Fixed
            <span className="ml-auto text-[10px] text-slate-400">→ Fixed</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onTriage('void')} className="text-red-600" data-testid={`triage-void-${claim.id}`}>
            <Prohibit size={14} className="mr-2" />
            Void the claim
            <span className="ml-auto text-[10px] text-red-300">→ Voided</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onTriage('resolve')} data-testid={`triage-resolve-${claim.id}`}>
            <CheckCircle size={14} className="mr-2 text-emerald-600" />
            Mark resolved
            <span className="ml-auto text-[10px] text-slate-400">audit only</span>
          </DropdownMenuItem>
        </>
      )}
      {claim.triage_status === 'resolved' && (
        <>
          <DropdownMenuSeparator />
          <div className="px-2 py-1 text-[10px] text-emerald-700 flex items-center gap-1">
            <CheckCircle size={12} weight="fill" />
            Resolved {claim.triaged_by_name ? `by ${claim.triaged_by_name}` : ''}
          </div>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

const ClaimsTableRow = ({
  claim, isSelected,
  onSelect, onEdit, onCopy, onSubmit, onDelete,
  onOpenAdjudication, onOpenTriage, onOpenCorrect, onOpenVoid, onMarkFixed,
}) => (
  <TableRow>
    <TableCell>
      {SELECTABLE_STATUSES.includes(claim.status) && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect(claim.id, checked)}
          data-testid={`select-claim-${claim.id}`}
        />
      )}
    </TableCell>
    <TableCell>
      <div>
        <span className="font-mono text-xs text-slate-600">{claim.claim_number}</span>
        <p className="md:hidden text-sm font-medium text-slate-900 mt-0.5">{claim.patient_name}</p>
      </div>
    </TableCell>
    <TableCell className="hidden md:table-cell">
      <p className="font-medium text-slate-900">{claim.patient_name}</p>
    </TableCell>
    <TableCell className="hidden lg:table-cell">
      <div>
        <span className="font-mono text-sm font-medium text-indigo-700">{claim.service_code}</span>
        {claim.modifier_1 && <span className="ml-1 text-xs text-slate-500">-{claim.modifier_1}</span>}
        <p className="text-xs text-slate-500 truncate max-w-[200px]">{claim.service_description}</p>
      </div>
    </TableCell>
    <TableCell className="hidden sm:table-cell">
      <div>
        {claim.service_end_date && claim.service_end_date > claim.service_date ? (
          <>
            <p className="text-sm">{formatDate(claim.service_date)} – {formatDate(claim.service_end_date)}</p>
            <p className="text-xs text-slate-500">
              {claim.total_hours || `${claim.units} units`}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm">{formatDate(claim.service_date)}</p>
            <p className="text-xs text-slate-500">
              {formatTime(claim.time_in)} - {formatTime(claim.time_out)}
            </p>
          </>
        )}
      </div>
    </TableCell>
    <TableCell className="hidden sm:table-cell"><span className="font-mono">{claim.units}</span></TableCell>
    <TableCell>
      <span className="font-mono font-medium text-sm">{formatCurrency(claim.estimated_reimbursement)}</span>
    </TableCell>
    <TableCell>
      <span className={`status-badge ${getStatusColor(claim.status)} text-xs`}>{claim.status}</span>
      {claim.edi_277_status_code ? (
        <Edi277Badge claim={claim} />
      ) : null}
      <ReasonChip claim={claim} onClick={onOpenAdjudication} />
    </TableCell>
    <TableCell>
      <RowActionsMenu
        claim={claim}
        onEdit={onEdit}
        onCopy={onCopy}
        onSubmit={onSubmit}
        onDelete={onDelete}
        onAdjudication={onOpenAdjudication}
        onTriage={onOpenTriage}
        onCorrect={onOpenCorrect}
        onVoid={onOpenVoid}
        onMarkFixed={onMarkFixed}
      />
    </TableCell>
  </TableRow>
);

export default ClaimsTableRow;
